/**
 * Budget Table Line Row Component
 *
 * Individual line item row in budget table
 * Displays item details with inline edit/delete actions
 *
 * Design specs from Figma:
 * - Height: 60px
 * - Padding: 16px horizontal
 * - Background: alternating based on isEven prop
 * - Hover: rgba(120, 70, 255, 0.1)
 * - Border-bottom: 1px solid rgba(93, 93, 93, 0.3)
 * - Font: 14px regular
 * - Columns: checkbox (50px), name (flex-1), 3x currency (160px), percent (100px), actions (100px)
 */

import { Eye, Pencil, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/class-control";
import { Checkbox } from "@/components/ui/checkbox";
import { BudgetLineItem } from "@/types/orcamentos.interface";

interface BudgetTableLineRowProps {
  /** Line item data */
  item: BudgetLineItem;
  /** View callback */
  onView: () => void;
  /** Edit callback */
  onEdit: () => void;
  /** Delete callback */
  onDelete: () => void;
  /** Whether this is an even-indexed row (for alternating backgrounds) */
  isEven: boolean;
  /** Checkbox checked state */
  checked: boolean;
  /** Checkbox change callback */
  onCheckChange: (checked: boolean) => void;
}

/**
 * Format currency to Brazilian Real
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

/**
 * Format percentage
 */
function formatPercent(value: number): string {
  return `${value}%`;
}

export function BudgetTableLineRow({
  item,
  onView,
  onEdit,
  onDelete,
  checked,
  onCheckChange,
}: BudgetTableLineRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract raw data arrays from transformation
  const rawData = (item as any)._rawData;
  const staffContracts = rawData?.staffContracts || [];

  // Flatten payments from all contracts for this item
  const payments = staffContracts.flatMap((contract: any) =>
    (contract.payments || []).map((payment: any) => ({
      ...payment,
      contractId: contract.id,
    }))
  );

  const hasChildren = payments.length > 0;

  return (
    <>
      <tr
        className={cn(
          "h-[60px] border-b border-[rgba(93,93,93,0.3)] transition-colors",
          "hover:bg-[rgba(120,70,255,0.1)]",
          "bg-[rgba(12,20,39,0.5)]"
        )}
      >
        {/* Checkbox & Expand Toggle column */}
        <td className="w-[80px] text-center px-[8px]">
          <div className="flex items-center justify-start gap-[8px]">
            {hasChildren ? (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-[#888888] hover:text-[#7846FF] transition-colors"
              >
                {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
            ) : (
              <div className="w-[18px]"></div>
            )}
            <Checkbox checked={checked} onCheckedChange={onCheckChange} />
          </div>
        </td>

        {/* Item name column */}
        <td className="px-[16px] text-[16px] font-semibold text-[#F6F6F6]">
          {item.name}
        </td>

        {/* Última atualização */}
        <td className="w-[160px] px-[16px] text-center text-[14px] font-normal text-[#F6F6F6]">
          {item.lastUpdate || "-"}
        </td>

        {/* Último orçamento aprovado */}
        <td className="w-[180px] px-[16px] text-right text-[14px] font-normal text-[#F6F6F6]">
          {formatCurrency(item.orcamentoPrevisto)}
        </td>

        {/* Orçamento Executado */}
        <td className="w-[180px] px-[16px] text-right text-[14px] font-normal text-[#F6F6F6]">
          {formatCurrency(item.value)}
        </td>

        {/* Percentual */}
        <td className="w-[100px] px-[16px] text-right text-[14px] font-normal text-[#F6F6F6]">
          {formatPercent(item.percentual)}
        </td>

        {/* Actions column */}
        <td className="w-[100px] px-[16px]">
          <div className="flex items-center justify-end gap-[8px]">
            {/* View button */}
            <button
              onClick={onView}
              className="text-[#888888] hover:text-[#7846FF] transition-colors p-[4px]"
              aria-label="Visualizar"
              title="Visualizar"
            >
              <Eye size={20} />
            </button>

            {/* Edit button */}
            <button
              onClick={onEdit}
              className="text-[#888888] hover:text-[#7846FF] transition-colors p-[4px]"
              aria-label="Editar"
              title="Editar"
            >
              <Pencil size={20} />
            </button>

            {/* Delete button */}
            <button
              onClick={onDelete}
              className="text-[#888888] hover:text-[#F1745B] transition-colors p-[4px]"
              aria-label="Deletar"
              title="Deletar"
            >
              <Trash2 size={20} />
            </button>
          </div>
        </td>
      </tr>

      {/* Child rows (Payments) */}
      {isExpanded && hasChildren && payments.map((payment: any, idx: number) => {
        // Calculate % of this payment relatively to the subitem's executed budget
        const paymentValue = Number(payment.amount) || 0;
        const subitemExecuted = item.value || 1; // avoid division by zero
        const percentual = paymentValue > 0 && subitemExecuted > 0
          ? Math.round((paymentValue / subitemExecuted) * 100)
          : 0;

        // Extract date and format
        const paymentDate = payment.date ? new Date(payment.date).toLocaleDateString("pt-BR") : "-";

        return (
          <tr
            key={`payment-${payment.id}-${idx}`}
            className="h-[50px] border-b border-[rgba(93,93,93,0.1)] transition-colors bg-[rgba(12,20,39,0.3)] hover:bg-[rgba(120,70,255,0.05)]"
          >
            {/* Indented Checkbox column (no checkbox for payments mostly) */}
            <td className="w-[80px] text-center px-[8px]"></td>

            {/* Item name column (Payment Description) */}
            <td className="px-[16px] pl-[32px] text-[14px] font-normal text-[#B0B0B0]">
              <div className="flex items-center gap-[8px]">
                <div className="w-[6px] h-[6px] rounded-full bg-[#5D5D5D]" />
                {payment.fiscal_doc_type || 'Nota Fiscal'} {payment.fiscal_doc_number ? `#${payment.fiscal_doc_number}` : ''}
                {payment.notes ? ` - ${payment.notes}` : ''}
              </div>
            </td>

            {/* Última atualização / Payment Date */}
            <td className="w-[160px] px-[16px] text-center text-[13px] font-normal text-[#B0B0B0]">
              {paymentDate}
            </td>

            {/* Último orçamento aprovado (Empty for payments usually, or could be the contract value) */}
            <td className="w-[180px] px-[16px] text-right text-[13px] font-normal text-[#B0B0B0]">
              -
            </td>

            {/* Orçamento Executado (Payment amount) */}
            <td className="w-[180px] px-[16px] text-right text-[13px] font-normal text-[#F6F6F6]">
              {formatCurrency(paymentValue)}
            </td>

            {/* Percentual (relative to parent) */}
            <td className="w-[100px] px-[16px] text-right text-[13px] font-normal text-[#B0B0B0]">
              {percentual}%
            </td>

            {/* Actions column (Empty for payments for now, as requested only subitems are editable) */}
            <td className="w-[100px] px-[16px]"></td>
          </tr>
        );
      })}
    </>
  );
}
